//
// transport.ts — cross-window channel between the embed (inside the
// customer's page) and our receiver app (loaded from our origin).
//
// The embed needs to:
//
//   1. Open a new window on our origin that will show the resulting
//      video. This MUST be done synchronously inside a user-gesture
//      handler (the Render click) or browsers will block it as a
//      popup. That's why openResultChannel is called from the click
//      handler, before any rendering starts.
//
//   2. Stream rendering progress and the final MP4 blob into that
//      window. Cross-origin postMessage handles everything: blobs
//      clone fine, and we can narrow targetOrigin to the app's origin
//      so nothing leaks.
//
// Because the receiver window has to bootstrap React before it can
// listen for messages, we buffer every outbound message until the
// receiver has signalled `mve/receiver-ready`. Anything sent before
// then would be dropped by the browser (no listener attached yet).
//
import type { AppToEmbed, EmbedToApp, RenderMeta, RenderStartMeta } from '@mve/protocol';
import { MVE_RESULT_PATH, isMveMessage } from '@mve/protocol';

//
// The app origin is baked in at build time by Vite's `define` config,
// so we don't have to read it from a config endpoint or hardcode it
// here. Points at http://localhost:5273 in dev and our production
// origin in prod builds.
//
declare const __MVE_APP_ORIGIN__: string;

//
// The interface the panel uses to push lifecycle events at the
// receiver window. sendX methods are fire-and-forget; they buffer
// until the receiver is ready and drop silently once the popup
// closes. isClosed() is what lets the render loop notice a user
// cancellation and abort. dispose() removes our message listener
// when the caller is done with the channel.
//
export interface ResultChannel {
  sendStart(meta: RenderStartMeta): void;
  sendProgress(fraction: number): void;
  sendResult(blob: Blob, meta: RenderMeta): void;
  sendError(message: string): void;
  isClosed(): boolean;
  dispose(): void;
}

//
// Optional handlers the caller registers when opening the channel.
// Currently the only one is `onRequestRender`, which fires when the
// receiver posts a `mve/request-render` message — the user picking
// a resolution in the popup and asking the embed for a fresh render.
//
export interface ResultChannelHandlers {
  onRequestRender?: (width: number) => void;
}

//
// Open the receiver window and return a ResultChannel over it, or
// null if the browser blocked the popup. Must be called inside a
// user-gesture callback (click, keydown, ...) — otherwise the popup
// blocker will reject window.open.
//
// The channel buffers messages until the receiver posts
// `mve/receiver-ready` on our (the opener's) window. That handshake
// is necessary because React in the popup takes a few hundred
// milliseconds to mount before it can install its `message` listener,
// during which any eager sends would be discarded.
//
export function openResultChannel(
  handlers: ResultChannelHandlers = {},
): ResultChannel | null {
  const origin = __MVE_APP_ORIGIN__;
  // Open as a popup *window* rather than a tab so the host page
  // can stay visible alongside it while rendering. The reason is
  // structural, not aesthetic: Chrome throttles
  // requestAnimationFrame to ~1 Hz in background tabs, and that
  // throttles both Mapbox's internal render loop AND our frame-
  // capture wait, making the render hang outright. With both
  // windows on screen the host tab keeps painting and the render
  // proceeds at full speed. Width/height are sized to comfortably
  // hold the receiver's video preview at most resolutions.
  //
  // Passing `popup=yes` (plus dimensions) is what makes Chrome
  // render this as a discrete window instead of a tab. _blank
  // remains as the target so we don't accidentally focus an
  // unrelated same-origin tab the user already has open.
  const popup = window.open(
    origin + MVE_RESULT_PATH,
    '_blank',
    'popup=yes,width=960,height=720',
  );
  if (!popup) return null;

  // Handshake state. Flipped to true once we see mve/receiver-ready.
  let ready = false;
  // Messages posted before the handshake completes get queued here
  // and replayed in order as soon as the receiver says it's listening.
  const buffer: EmbedToApp[] = [];

  const flush = (): void => {
    for (const msg of buffer) {
      if (popup.closed) break;
      popup.postMessage(msg, origin);
    }
    buffer.length = 0;
  };

  const send = (msg: EmbedToApp): void => {
    if (popup.closed) return;
    if (ready) popup.postMessage(msg, origin);
    else buffer.push(msg);
  };

  //
  // Listen on our (the opener's) window for the receiver's ready
  // signal. Enforce origin strictly — postMessage will happily deliver
  // events from any window, and we don't want to act on messages from
  // unrelated frames that happen to look like our protocol.
  //
  const onMessage = (event: MessageEvent): void => {
    if (event.origin !== origin) return;
    if (!isMveMessage(event.data)) return;
    const msg = event.data as AppToEmbed;
    if (msg.kind === 'mve/receiver-ready') {
      ready = true;
      flush();
      return;
    }
    if (msg.kind === 'mve/request-render') {
      handlers.onRequestRender?.(msg.width);
      return;
    }
  };

  window.addEventListener('message', onMessage);

  return {
    sendStart(meta) { send({ kind: 'mve/render-start', meta }); },
    sendProgress(fraction) { send({ kind: 'mve/render-progress', fraction }); },
    sendResult(blob, meta) { send({ kind: 'mve/render-result', blob, meta }); },
    sendError(message) { send({ kind: 'mve/render-error', message }); },
    isClosed: () => popup.closed,
    dispose() { window.removeEventListener('message', onMessage); },
  };
}
